import { useState } from "react";
import { CheckCircle2, Clock, Loader2, MapPin, Mail, Phone, Truck } from "lucide-react";
import { eur } from "../../lib/api";
import { ShipForm } from "./ShipForm";

const when = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export const OrderCard = ({ o, onMarkPaid, onShip }) => {
  const [busy, setBusy] = useState(false);
  const [shipping, setShipping] = useState(false);
  const c = o.customer || {};
  const a = c.address || {};
  const paid = o.payment_status === "paid";
  const mark = async () => {
    if (!window.confirm(`¿Confirmas que ha llegado la transferencia de ${eur(o.amount)} con concepto ${o.ref}?`)) return;
    setBusy(true);
    await onMarkPaid(o.id);
    setBusy(false);
  };
  return (
    <article data-testid={`order-${o.id}`} className="rounded-[14px] border border-[#8CD05A]/20 bg-[#0E2A1C] p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono-d text-lg">{o.ref}</p>
          <p className="text-xs text-[#C9D6C2]">{when(o.created_at)} · {o.method}</p>
        </div>
        <div className="text-right">
          <p className="font-serif-d text-3xl">{eur(o.amount ?? 0)}</p>
          <span data-testid={`order-status-${o.id}`} className={`inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 mt-1 ${paid ? "bg-[#8CD05A]/20 text-[#B9E79A]" : "bg-[#F2B441]/20 text-[#F2B441]"}`}>
            {paid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />} {paid ? "Pagado" : "Pendiente de transferencia"}
          </span>
        </div>
      </header>
      <div className="grid sm:grid-cols-2 gap-5 mt-5">
        <div className="text-sm space-y-1.5">
          <p className="font-semibold">{c.name || "—"}{c.business ? ` · ${c.business}` : ""}{c.dni ? ` · ${c.dni}` : ""}</p>
          <p className="flex gap-2 text-[#E3EADF]"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#8CD05A]" />
            <span>{[a.line1, a.line2].filter(Boolean).join(", ") || "Sin dirección"}<br />{[a.postal_code, a.city].filter(Boolean).join(" ")}{a.state ? ` (${a.state})` : ""}{a.country ? ` · ${a.country}` : ""}</span></p>
          {c.email && <p className="flex gap-2 text-[#E3EADF] break-all"><Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#8CD05A]" />{c.email}</p>}
          {c.phone && <p className="flex gap-2 text-[#E3EADF]"><Phone className="w-4 h-4 mt-0.5 shrink-0 text-[#8CD05A]" />{c.phone}</p>}
        </div>
        <ul className="text-sm space-y-1.5">
          {o.items.map((it, i) => (
            <li key={i} className="flex justify-between gap-3"><span>{it.name} × {it.quantity}</span><span className="font-mono-d">{it.line_total != null ? eur(it.line_total) : ""}</span></li>
          ))}
          <li className="flex justify-between gap-3 text-[#C9D6C2]"><span>Envío</span><span className="font-mono-d">{o.shipping ? eur(o.shipping) : "Gratis"}</span></li>
        </ul>
      </div>
      {paid && o.tracking && (
        <p data-testid={`order-tracking-${o.id}`} className="mt-4 text-sm flex flex-wrap items-center gap-2 text-[#B9E79A]">
          <Truck className="w-4 h-4" /> Enviado el {when(o.shipped_at)} · {o.tracking.carrier} · <span className="font-mono-d break-all">{o.tracking.number}</span>
          {o.tracking.url && <a href={o.tracking.url} target="_blank" rel="noreferrer" className="underline underline-offset-4">seguimiento</a>}
        </p>
      )}
      {paid && !shipping && (
        <button type="button" onClick={() => setShipping(true)} data-testid={`order-ship-${o.id}`}
          className={`mt-5 h-11 px-5 rounded-full font-semibold inline-flex items-center gap-2 transition-colors ${o.tracking ? "border border-[#F6EEDC]/25 text-sm hover:border-[#F2B441]" : "bg-[#F2B441] text-[#0E2A1C] hover:bg-[#8CD05A]"}`}>
          <Truck className="w-4 h-4" /> {o.tracking ? "Cambiar seguimiento" : "Enviado"}
        </button>
      )}
      {paid && shipping && (
        <ShipForm id={o.id} initial={o.tracking} onCancel={() => setShipping(false)}
          onSubmit={async (t) => { const ok = await onShip(o.id, t); if (ok) setShipping(false); return ok; }} />
      )}
      {!paid && o.provider === "transfer" && (
        <button type="button" onClick={mark} disabled={busy} data-testid={`order-mark-paid-${o.id}`}
          className="mt-5 h-11 px-5 rounded-full bg-[#8CD05A] text-[#0E2A1C] font-semibold inline-flex items-center gap-2 hover:bg-[#F2B441] transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Marcar como pagado
        </button>
      )}
    </article>
  );
};
